import { Box, Flex, HStack, Text, VStack } from '@skin-seoul/design-system';
import React from 'react';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';

interface ReviewHeaderProps {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: number]: number;
  };
  onlyScore?: boolean;
  onlyDistribution?: boolean;
  customerImages?: ReviewImage[];
  reviews?: Review[];
  onViewAllImages?: () => void;
  onWriteReview: () => void;
  isWriteReviewDisabled: boolean;
}

import type { ComponentProps } from 'react';
import { LucideIcon } from '../../../components/LucideIcon';

type BoxProps = ComponentProps<typeof Box>;

const ProgressBar = ({ percentage, ...props }: { percentage: number } & Omit<BoxProps, 'children'>) => (
  <Box
    flexGrow="1"
    w={{
      base: 'full',
      tablet: '357px',
    }}
    h="5px"
    bg="neutral.100"
    borderRadius="4px"
    overflow="hidden"
    position="relative"
    _after={{
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: `${percentage}%`,
      bg: 'neutral.700',
      borderRadius: '4px',
    }}
    {...props}
  />
);

export const ReviewHeader = ({ averageRating, totalReviews, ratingDistribution }: ReviewHeaderProps) => {
  const getDistributionPercentage = (rating: number): number => {
    const count = ratingDistribution[rating] || 0;
    return (count / totalReviews) * 100;
  };

  return (
    <VStack
      gap={{
        base: 6,
        tablet: 4,
      }}
      align="flex-start"
    >
      <Flex direction={{ base: 'column', tablet: 'row' }} gap={6} justify="space-between" w="full">
        <VStack gap={2} align="flex-start">
          <HStack gap={1}>
            <StarIcon width={36} height={36} />
            <Text textStyle="display.large" fontWeight="bold" color="primary.400">
              {averageRating.toFixed(1)}
            </Text>
          </HStack>
          {totalReviews && (
            <Text textStyle="label.regular" fontWeight="medium" color="neutral.500">
              {totalReviews === 1 ? `${totalReviews} review` : `${totalReviews} reviews`}
            </Text>
          )}
        </VStack>

        <Box
          w={{
            base: 'full',
            tablet: 'fit-content',
          }}
        >
          <VStack gap={3} align="flex-start" w="full">
            {Object.entries(ratingDistribution)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([rating]) => (
                <HStack key={rating} gap={3} align="center" w="full">
                  <Text textStyle="paragraph.small" fontWeight="medium" color="neutral.700" flexShrink={0}>
                    {rating} Star
                  </Text>
                  <ProgressBar percentage={getDistributionPercentage(Number(rating))} />
                  <Text
                    textStyle="paragraph.regular"
                    fontWeight="medium"
                    color="neutral.700"
                    flexShrink={0}
                    w="45px"
                    textAlign="center"
                  >
                    {parseInt(getDistributionPercentage(Number(rating)).toString(), 10)}%
                  </Text>
                </HStack>
              ))}
          </VStack>
        </Box>
      </Flex>
      <HStack align="center" gap={1}>
        <LucideIcon
          name="Info"
          color="neutral.500"
          width={{
            base: 4,
            tablet: 5,
          }}
          height={{
            base: 4,
            tablet: 5,
          }}
        />
        <Text
          textStyle={{
            base: 'paragraph.xsmall',
            tablet: 'paragraph.small',
          }}
          fontWeight="medium"
          color="neutral.500"
        >
          All the reviews in SkinSeoul are verified.
        </Text>
      </HStack>
    </VStack>
  );
};

export default ReviewHeader;
